import { useMemo } from "react";
import { CheckPicker } from "rsuite";
import { useAtomValue } from "jotai";
import { difference } from "underscore";

import { modulesAtom } from "../../../atoms/GlobalAtoms";
import { FlowChartConfigI } from "../../../types/PageInterfaces";
import CollapseItem from "../../FormBuilder/components/CollapseItem";
import useModule from "../../../hooks/useModule";
import RequiredTitle from "../../../elements/RequiredTitle";
import ManageItems from "../../ManageItems/ManageItems";

interface Props {
  flowChartConfig: FlowChartConfigI;
  setFlowChartConfig: (config: FlowChartConfigI) => void;
  containerRef?: any;
}

export default function FlowChartConfig({
  flowChartConfig,
  setFlowChartConfig,
  containerRef,
}: Props) {
  const modules = useAtomValue(modulesAtom);
  const { getModule } = useModule();

  const filteredModules = useMemo(() => {
    return modules?.filter((m) => !m.Default) || [];
  }, [modules]);

  const selectedModules = Object.keys(flowChartConfig.Modules);

  return (
    <div>
      <RequiredTitle title="Data Sources" size="md" className="mb--2" />
      <small>
        Pick data sources for the flow builder; their records can then be added
        as nodes
      </small>
      <CheckPicker
        container={containerRef ? () => containerRef.current : undefined}
        preventOverflow
        value={selectedModules}
        className="my-2"
        placeholder="Select Data Sources"
        data={filteredModules}
        block
        labelKey="Name"
        valueKey="APIName"
        onSelect={(newModules) => {
          const currentModules = Object.keys(flowChartConfig.Modules);

          const addedModules = difference(newModules, currentModules);
          const removedModules = difference(currentModules, newModules);

          if (addedModules.length === 1) {
            setFlowChartConfig({
              Modules: {
                ...flowChartConfig?.Modules,
                [addedModules[0]]: { Fields: [] },
              },
            });
          } else if (removedModules.length === 1) {
            const newFlowChartConfig = structuredClone(flowChartConfig);

            delete newFlowChartConfig.Modules[removedModules[0]];

            setFlowChartConfig(newFlowChartConfig);
          }

          return [];
        }}
      />
      {selectedModules.map((moduleAPIName) => {
        const mod = getModule(moduleAPIName);

        if (!mod) return null;

        const fieldsConfig =
          flowChartConfig.Modules?.[moduleAPIName]?.Fields || [];

        const fields = fieldsConfig.map((f) =>
          mod.Fields.find((field: any) => field?.APIName === f)
        );

        return (
          <CollapseItem
            key={mod.APIName}
            className="bg-ultralight"
            title={mod.Name}
            onDelete={() => {
              delete flowChartConfig?.Modules[moduleAPIName];
              setFlowChartConfig(flowChartConfig);
            }}
          >
            <h4 className="mb-0">Node Fields</h4>
            <small>
              Select the fields to display when adding a node that utilizes this
              data source
            </small>
            <CheckPicker
              placeholder="Select Fields"
              data={mod.Fields}
              labelKey="Name"
              valueKey="APIName"
              value={fieldsConfig}
              onSelect={(fields) => updateFields(moduleAPIName, fields)}
              className="mb-2 mt-2"
              preventOverflow
            />
            <ManageItems
              // @ts-ignore
              idKey="APIName"
              itemKey={undefined}
              items={fields}
              onDelete={({ id }) => deleteField(moduleAPIName, id)}
              onReorder={({ newOrder }) =>
                updateFields(moduleAPIName, newOrder as string[])
              }
              description="No fields have been added."
              descriptionCSS="mb-2"
            />
          </CollapseItem>
        );
      })}
    </div>
  );

  function updateFields(moduleAPIName: string, newFields: string[]) {
    setFlowChartConfig({
      Modules: {
        ...flowChartConfig.Modules,
        [moduleAPIName]: {
          ...flowChartConfig.Modules[moduleAPIName],
          Fields: newFields,
        },
      },
    });
  }

  function deleteField(moduleAPIName: string, fieldToDelete: string) {
    setFlowChartConfig({
      Modules: {
        ...flowChartConfig.Modules,
        [moduleAPIName]: {
          ...flowChartConfig.Modules[moduleAPIName],
          Fields: flowChartConfig.Modules[moduleAPIName].Fields.filter(
            (f) => f !== fieldToDelete
          ),
        },
      },
    });
  }
}
